import { useState, useEffect, useCallback, useRef } from 'react';

// Reusable hook for paginated resources with loading, error, total count, and infinite-scroll
export const useInfiniteCursorResource = ({ fetchFn }) => {
	const [data, setData] = useState([]);
	const [nextCursor, setNextCursor] = useState(null);
	const [totalCount, setTotalCount] = useState(null); // null = not loaded yet, number = loaded count
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [error, setError] = useState(null);
	const dataRef = useRef(data);
	const nextCursorRef = useRef(nextCursor);

	dataRef.current = data;
	nextCursorRef.current = nextCursor;

	const loadPage = useCallback(
		async (mode = 'initial') => {
			try {
				if (mode === 'initial') setLoading(true);
				if (mode === 'loadMore') setLoadingMore(true);

				setError(null);

				const response = await fetchFn(nextCursorRef.current);

				const records = response?.data ?? [];
				const updatedNextCursor = response?.nextCursor ?? null;
				const total = Number(response?.totalCount ?? 0);

				setNextCursor(updatedNextCursor);
				setTotalCount(total);

				setData((prev) => {
					let nextData = [];
					if (mode === 'initial') {
						nextData = records
					} else {
						const prevIds = prev.map(obj => obj.id);
						const dedupedRecords = records.filter(record => !prevIds.includes(record.id));
						nextData = [...prev, ...dedupedRecords]
					}

					// An empty page or an absent nextCursor means the server has
					// no more records, even if totalCount over-reports — otherwise
					// loadMore keeps requesting the first page forever
					setHasMore(
						records.length > 0 && nextData.length < total && !!updatedNextCursor
					);

					return nextData;
				});
			} catch (err) {
				setError(err?.message ?? null);
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[fetchFn]
	);

	useEffect(() => {
		// Reset pagination when the resource (fetchFn) changes so a later
		// loadMore continues from page 2 of the new resource
		setNextCursor(null);
		nextCursorRef.current = null;
		loadPage();
	}, [loadPage]);

	const loadMore = useCallback(() => {
		if (!loadingMore && !loading && hasMore) {
			loadPage('loadMore');
		}
	}, [loading, loadingMore, hasMore, loadPage]);

	// Removes the first item matching `matcher` (a predicate fn) without refetching.
	// Returns { item, index } so the caller can restore it later via insertItem,
	// or null if nothing matched.
	const removeItem = useCallback((matcher) => {
		const current = dataRef.current;
		const index = current.findIndex(matcher);

		if (index === -1) return null;

		const item = current[index];
		
		// Update list and total count
		dataRef.current = current.filter((_, i) => i !== index);
		setData(dataRef.current);
		setTotalCount((prev) => (prev === null ? prev : Math.max(0, prev - 1)));

		// Used for undo action if you want element to go back to its original position
		return { item, index };
	}, []);

	// Inserts `item` at `index` (appended when omitted) without refetching.
	const insertItem = useCallback((item, index) => {
		const current = dataRef.current;
		const at = index === undefined ? current.length : Math.min(Math.max(index, 0), current.length);

		dataRef.current = [...current.slice(0, at), item, ...current.slice(at)];
		setData(dataRef.current);
		setTotalCount((prev) => (prev === null ? prev : prev + 1));
	}, []);

	return {
		data,
		removeItem,
		insertItem,
		totalCount,
		loading, // initial load
		loadingMore, // pagination
		error,
		hasMore,
		loadMore,
	};
};
